import { indexedDbQueueStorage } from "@tovi/pos-sync/indexeddb";
import {
  MemoryQueueStorage,
  OutboundQueue,
  syncState,
  type PosMode,
  type QueuedOperation,
  type RejectedOperation,
  type SendResult,
  type SyncState,
} from "@tovi/pos-sync";
import { api, baseUrl, signOut, token } from "./api.ts";
import { staffToken } from "./staff.ts";

/**
 * The till's one long-lived object: what is queued, what mode it is in, and
 * whether anything needs a person.
 *
 * A store rather than a hook so the queue survives every re-render, every
 * screen change and — through IndexedDB — the app being killed mid-service.
 * Screens subscribe; nothing owns it.
 */

type Listener = () => void;

/** Told what the server said, for something this till sent. */
export type SentListener = (operation: QueuedOperation, response: unknown) => void;
/** Told that something this till sent was refused for good. */
export type RejectedListener = (operation: QueuedOperation, message: string) => void;

export class Till {
  readonly outbound: OutboundQueue;
  #listeners = new Set<Listener>();
  #mode: PosMode = "online";
  /** Set the moment anything is queued with no way to send it. */
  #tookWorkOffline = false;
  #queued = 0;
  #draining = false;
  /**
   * Read out of the queue's dead-letter store, never accumulated here. This
   * field is a cache of what is durable, so a reload shows the same refused
   * settlements it showed before — the previous version was the only copy,
   * and closing the tab lost somebody's money quietly.
   */
  #failures: RejectedOperation[] = [];
  /**
   * The last state object, kept rather than rebuilt.
   *
   * `useSyncExternalStore` compares snapshots by identity: a `state()` that
   * returned a fresh object each call re-rendered forever. Found by opening
   * the till.
   */
  #snapshot: SyncState = { kind: "idle" };

  #sent = new Set<SentListener>();
  #rejected = new Set<RejectedListener>();

  constructor(storage = defaultStorage()) {
    this.outbound = new OutboundQueue(storage, transport, (operation, response) => {
      for (const listener of this.#sent) listener(operation, response);
    });
  }

  /**
   * Hear what the server answered for something this till sent.
   *
   * How a bill learns its own id. It is opened when a table is seated and
   * added to for as long as the party sits there, and by then the operation
   * that created it is long gone from the queue — so the id has to be kept
   * rather than depended on.
   */
  onSent(listener: SentListener): () => void {
    this.#sent.add(listener);
    return () => this.#sent.delete(listener);
  }

  /** Hear that something was refused, so whatever it belonged to can say so. */
  onRejected(listener: RejectedListener): () => void {
    this.#rejected.add(listener);
    return () => this.#rejected.delete(listener);
  }

  /**
   * Starts the queue, and answers with the way to stop it again.
   *
   * The teardown is not ceremony: without it React's development double-mount
   * leaves the first interval running forever, so a till drains twice on every
   * tick and nothing ever clears either timer.
   */
  async start(): Promise<() => void> {
    await this.outbound.resume();
    await this.#refresh();

    // The network is a hint, not the mode: `08` § Offline mode makes switching
    // an explicit decision in both directions, because a till that decided for
    // itself would silently stop receiving other terminals' orders.
    const wake = () => void this.drain();
    globalThis.addEventListener?.("online", wake);
    const ticking = setInterval(wake, 15_000);

    return () => {
      globalThis.removeEventListener?.("online", wake);
      clearInterval(ticking);
    };
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  get mode(): PosMode {
    return this.#mode;
  }

  get failures(): RejectedOperation[] {
    return this.#failures;
  }

  state(): SyncState {
    return this.#snapshot;
  }

  setMode(mode: PosMode): void {
    this.#mode = mode;
    this.#emit();
    if (mode === "online") void this.drain();
  }

  /**
   * Everything that changes something goes through here.
   *
   * Queued first, sent second — so the cashier's next tap never waits on a
   * network, and a service taken through a dead connection is a service that
   * simply sends later.
   */
  /**
   * Queues an operation without sending it.
   *
   * Used when two operations belong together — a bill and its payment — so
   * that the second is already waiting when the first is sent and can be
   * resolved into. Queueing them either side of a drain leaves the payment
   * pointing at an id that has already gone past.
   */
  async queue(
    path: string,
    options: {
      method: QueuedOperation["method"];
      body?: unknown;
      isMoney?: boolean;
      dependsOn?: QueuedOperation["dependsOn"];
    },
    /**
     * The operation's own id, when the caller needs to choose it.
     *
     * It travels as `Idempotency-Key`, so re-queueing a create under the id it
     * had the first time asks for the same bill back rather than a second one.
     * That is what makes a bill recoverable when the app is killed between the
     * create going out and its id being written down.
     */
    id: string = crypto.randomUUID(),
  ): Promise<string> {
    if (this.#mode === "offline") this.#tookWorkOffline = true;
    // The cashier as they stood when the till did this, not as they stand
    // when the queue drains.
    await this.outbound.enqueue({ ...options, path, staffToken: staffToken() }, id, new Date());
    await this.#refresh();
    return id;
  }

  async send(
    path: string,
    options: {
      method: QueuedOperation["method"];
      body?: unknown;
      isMoney?: boolean;
      dependsOn?: QueuedOperation["dependsOn"];
    },
  ): Promise<string> {
    const id = await this.queue(path, options);
    if (this.#mode !== "offline") await this.drain();
    return id;
  }

  async drain(): Promise<void> {
    if (this.#mode === "offline") return;

    // Nothing can be sent without a credential, and `start`'s interval does
    // not stop while a till waits to be signed back in — so a terminal left
    // on the sign-in screen would put a doomed request on the wire every
    // fifteen seconds. The queue is kept and the drain resumes from
    // `onSignedIn`, which has a token by the time it calls.
    if (token() === null) return;

    this.#draining = true;
    this.#emit();

    // The whole drain is flagged, not each operation: a queue built offline is
    // replayed as one, and an operation sent the instant it was queued is not
    // a replay of anything.
    replayingOfflineWork = this.#tookWorkOffline;
    const report = await this.outbound.drain();
    replayingOfflineWork = false;

    // A refusal is somebody's decision to make, not something to retry into
    // the void — most of all when it was a settlement. The queue has already
    // written each one to durable storage by the time it hands it back; the
    // listeners are how a screen hears about it in this session.
    for (const rejected of report.rejected) {
      for (const listener of this.#rejected) listener(rejected.operation, rejected.message);
    }

    // Cleared only once the queue is actually empty. A drain that stopped
    // because the network went again is still a queue of offline work, and the
    // next attempt is still a replay.
    if ((await this.outbound.pending()).length === 0) this.#tookWorkOffline = false;

    this.#draining = false;
    await this.#refresh();
  }

  /** Take back something not yet sent. See `OutboundQueue.discard`. */
  async discard(id: string): Promise<QueuedOperation[]> {
    const removed = await this.outbound.discard(id);
    if (removed.length > 0) await this.#refresh();
    return removed;
  }

  /** A person has dealt with it. Out of storage, not just off the screen. */
  async dismiss(id: string): Promise<void> {
    await this.outbound.dismissRejection(id);
    await this.#refresh();
  }

  async #refresh(): Promise<void> {
    this.#queued = (await this.outbound.pending()).length;
    this.#failures = await this.outbound.rejected();
    this.#emit();
  }

  #emit(): void {
    const next = syncState({
      mode: this.#mode,
      queued: this.#queued,
      failures: this.#failures.length,
      isDraining: this.#draining,
    });
    // Only a real change is a change: an identical snapshot with a new
    // identity is what sent React round the loop in the first place.
    if (JSON.stringify(next) !== JSON.stringify(this.#snapshot)) {
      this.#snapshot = next;
    }
    for (const listener of this.#listeners) listener();
  }
}

function defaultStarter(): boolean {
  return typeof indexedDB !== "undefined";
}

function defaultStorage() {
  return defaultStarter() ? indexedDbQueueStorage(indexedDB) : new MemoryQueueStorage();
}

/**
 * Whether the queue currently being drained was built while disconnected.
 *
 * Module-scoped because `transport` is a function the queue holds, not a
 * method on the till — and only the till knows that it went offline and came
 * back. The API refuses a replay from a merchant whose plan does not include
 * `OFFLINE_CASHIER_AVAILABLE`, so it has to be told which drains are replays.
 */
let replayingOfflineWork = false;

const transport = async (operation: QueuedOperation): Promise<SendResult> => {
  try {
    const held = token();
    const response = await fetch(`${baseUrl()}/api${operation.path}`, {
      method: operation.method,
      headers: {
        "Content-Type": "application/json",
        ...(held ? { Authorization: `Bearer ${held}` } : {}),
        "Idempotency-Key": operation.id,
        // The cashier who took it, captured at enqueue. The server measures it
        // against `queuedAt` rather than now, so a queue that drained late
        // replays with exactly the authority it was taken under.
        ...(operation.staffToken ? { "X-Staff-Session": operation.staffToken } : {}),
        // Said plainly rather than inferred from a timestamp: work taken while
        // the terminal could not reach the server is the feature a merchant
        // buys, and the server should not have to guess which drains are which.
        ...(replayingOfflineWork ? { "X-Tovi-Offline-Replay": operation.queuedAt } : {}),
      },
      ...(operation.body === null ? {} : { body: JSON.stringify(operation.body) }),
    });

    /**
     * A 401 is not a refusal of *this* operation — nobody is allowed to send
     * anything until the terminal signs in again. Treating it as a rejection
     * would drop the bill and hand a cashier an error about a settlement that
     * was perfectly good, so it takes the offline path instead: the operation
     * stays queued, the drain stops with the order intact, and the sign-out
     * puts the sign-in screen in front of somebody.
     */
    if (response.status === 401) {
      signOut();
      return { outcome: "offline" };
    }

    // A 5xx is the server having a bad day, which is worth retrying. A 4xx is
    // an answer, and retrying it forever would hide it.
    if (response.status >= 500) return { outcome: "offline" };
    if (response.status >= 400) {
      const body = (await response.json().catch(() => null)) as {
        error?: { message?: string };
      } | null;
      return {
        outcome: "rejected",
        status: response.status,
        message: body?.error?.message ?? "The server refused it.",
      };
    }
    return { outcome: "sent", response: await response.json().catch(() => null) };
  } catch {
    // fetch throws only when the network did.
    return { outcome: "offline" };
  }
};

export { api };
